import { ProductPriceData } from "@domain/product/product-price-data";

type ProductListPriceProduct = {
  priceData?: ProductPriceData;
  zrefPriceData?: ProductPriceData;
  price?: ProductPriceData;
};

export function getProductListPriceData(product: ProductListPriceProduct, showZrefPrices: boolean): ProductPriceData | undefined {
  return showZrefPrices ? product.zrefPriceData : (product.priceData ?? product.price);
}

export function hasDiscountableProductListPrice(priceData: ProductPriceData | undefined): boolean {
  return !!priceData && priceData.price > 0;
}

export function isProductListDiscountEditable(
  canEditDiscounts: boolean,
  showCosts: boolean,
  priceData: ProductPriceData | undefined,
  hasDiscountKey: boolean,
): boolean {
  return canEditDiscounts && showCosts && hasDiscountableProductListPrice(priceData) && hasDiscountKey;
}

export function formatProductListDiscount(discount: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(discount);
}

export function normalizeProductListDiscountDisplay(
  value: string,
  currentDiscount: number,
  locale: string,
): { formattedValue: string; discount: number | undefined } {
  const discount = normalizeProductListDiscountInput(value);
  return {
    formattedValue: formatProductListDiscount(discount ?? currentDiscount, locale),
    discount,
  };
}

export function isValidProductListDiscount(discount: number): boolean {
  return Number.isFinite(discount) && discount >= 0 && discount <= 100;
}

export function normalizeProductListDiscountInput(value: string): number | undefined {
  const normalizedValue = value.trim().replace(",", ".");
  if (normalizedValue === "") {
    return 0;
  }

  const discount = Number(normalizedValue);
  return isValidProductListDiscount(discount) ? discount : undefined;
}

export function calculateDiscountedPrice(price: number, discount: number): number {
  if (!isValidProductListDiscount(discount)) {
    return price;
  }

  return price * (1 - discount / 100);
}

export function calculateDiscountedTotal(price: number, quantity: number, discount: number): number {
  return calculateDiscountedPrice(price, discount) * quantity;
}
